import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { Delete, Edit, Add, Search } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { Address, CreateAddressRequest } from '../types/addressBook';

interface AddressBookProps {
  open: boolean;
  onClose: () => void;
  onSelectAddress: (address: Address) => void;
}

const AddressBook: React.FC<AddressBookProps> = ({ open, onClose, onSelectAddress }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [filteredAddresses, setFilteredAddresses] = useState<Address[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAddressType, setSelectedAddressType] = useState<'all' | 'residential' | 'commercial'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);

  // Form state for adding/editing addresses
  const [formData, setFormData] = useState<CreateAddressRequest>({
    name: '',
    company: '',
    recipientName: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'US',
    phoneNumber: '',
    emailAddress: '',
    addressType: 'residential',
  });

  useEffect(() => {
    if (open) {
      loadAddresses();
    }
  }, [open]);

  useEffect(() => {
    filterAddresses();
  }, [addresses, searchQuery, selectedAddressType]);

  const loadAddresses = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAllAddresses();
      setAddresses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load addresses');
      console.error('Error loading addresses:', err);
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAddresses = () => {
    const list = Array.isArray(addresses) ? addresses : [];
    let filtered = list;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(addr =>
        addr.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.recipientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addr.state.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by address type
    if (selectedAddressType !== 'all') {
      filtered = filtered.filter(addr => addr.addressType === selectedAddressType);
    }

    setFilteredAddresses(filtered);
  };

  const handleAddAddress = async () => {
    try {
      const newAddress = await apiService.createAddress(formData);
      setAddresses(prev => [newAddress, ...prev]);
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError('Failed to add address');
      console.error('Error adding address:', err);
    }
  };

  const handleUpdateAddress = async () => {
    if (!editingAddress) return;

    try {
      const updatedAddress = await apiService.updateAddress(editingAddress.id, {
        ...formData,
        id: editingAddress.id,
      });
      if (updatedAddress) {
        setAddresses(prev => prev.map(addr => 
          addr.id === editingAddress.id ? updatedAddress : addr
        ));
        setEditingAddress(null);
        resetForm();
      }
    } catch (err) {
      setError('Failed to update address');
      console.error('Error updating address:', err);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    try {
      await apiService.deleteAddress(id);
      setAddresses(prev => prev.filter(addr => addr.id !== id));
    } catch (err) {
      setError('Failed to delete address');
      console.error('Error deleting address:', err);
    }
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setFormData({
      name: address.name,
      company: address.company || '',
      recipientName: address.recipientName,
      street1: address.street1,
      street2: address.street2 || '',
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      countryCode: address.countryCode,
      phoneNumber: address.phoneNumber || '',
      emailAddress: address.emailAddress || '',
      addressType: address.addressType,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      recipientName: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: '',
      countryCode: 'US',
      phoneNumber: '',
      emailAddress: '',
      addressType: 'residential',
    });
  };

  const handleSelectAddress = (address: Address) => {
    onSelectAddress(address);
    onClose();
  };

  const formatAddress = (address: Address) => {
    const parts = [
      address.recipientName,
      address.street1,
      address.street2,
      `${address.city}, ${address.state} ${address.postalCode}`,
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Address Book</Typography>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm || !!editingAddress}
          >
            Add Address
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search and Filter */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                placeholder="Search addresses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Address Type</InputLabel>
                <Select
                  value={selectedAddressType}
                  label="Address Type"
                  onChange={(e) => setSelectedAddressType(e.target.value as any)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Address List */}
        {!showAddForm && !editingAddress && (
          <List>
            {(Array.isArray(filteredAddresses) ? filteredAddresses : []).map((address) => (
              <React.Fragment key={address.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">{address.name}</Typography>
                        <Chip
                          label={address.addressType}
                          size="small"
                          color={address.addressType === 'residential' ? 'primary' : 'secondary'}
                        />
                      </Box>
                    }
                    secondary={formatAddress(address)}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleSelectAddress(address)}
                      sx={{ mr: 1 }}
                    >
                      <Add />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleEditAddress(address)}
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteAddress(address.id)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {(Array.isArray(filteredAddresses) ? filteredAddresses : []).length === 0 && !loading && (
              <ListItem>
                <ListItemText
                  primary="No addresses found"
                  secondary="Try adding a new address or adjusting your search criteria"
                />
              </ListItem>
            )}
          </List>
        )}

        {/* Add/Edit Form */}
        {(showAddForm || editingAddress) && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Recipient Name"
                  value={formData.recipientName}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company (Optional)"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.street1}
                  onChange={(e) => setFormData(prev => ({ ...prev, street1: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 2 (Optional)"
                  value={formData.street2}
                  onChange={(e) => setFormData(prev => ({ ...prev, street2: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postalCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email (Optional)"
                  value={formData.emailAddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, emailAddress: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Address Type</InputLabel>
                  <Select
                    value={formData.addressType}
                    label="Address Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, addressType: e.target.value as any }))}
                  >
                    <MenuItem value="residential">Residential</MenuItem>
                    <MenuItem value="commercial">Commercial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {showAddForm && (
          <>
            <Button onClick={() => { setShowAddForm(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddAddress} variant="contained">
              Add Address
            </Button>
          </>
        )}
        {editingAddress && (
          <>
            <Button onClick={() => { setEditingAddress(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAddress} variant="contained">
              Update Address
            </Button>
          </>
        )}
        {!showAddForm && !editingAddress && (
          <Button onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddressBook;
